import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, User, Camera, Copy, ExternalLink, Edit2, Crown, Trash2, Info, Phone, AtSign, MapPin, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";

const empty = { full_name: "", id_number: "", id_photo: "", whatsapp: "", address: "", maps_link: "", social_media: "", acquisition_source: "Instagram", referred_by_code: "", is_member: false, commission_percent: 0 };

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

export default function Customers() {
  const { user } = useAuth();
  const { settings } = useTheme();
  const SOURCES = [...(settings?.acquisition_sources || []), "Lainnya"];
  const isOwner = user?.role === "owner";
  const [items, setItems] = useCached("customers", []);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoCust, setInfoCust] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/customers").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const openInfo = async (c) => {
    setInfoCust(c); setInfoOpen(true); setInfoLoading(true);
    try { const r = await api.get(`/customers/${c.id}`); setInfoCust(r.data); }
    catch { /* keep list data */ }
    finally { setInfoLoading(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, id_photo: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Nama wajib diisi");
    try {
      const { id, referral_code, points, commission_earned, created_at, ...payload } = form;
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Data diperbarui" : "Pelanggan terdaftar");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal simpan"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Pindahkan "${c.full_name}" ke Sampah? Bisa dipulihkan dalam 30 hari.`)) return;
    await api.delete(`/customers/${c.id}`); toast.success("Dipindahkan ke Sampah");
    setInfoOpen(false); load();
  };

  const toggleMember = async (c, isMember, percent) => {
    try {
      await api.put(`/customers/${c.id}/member`, { is_member: isMember, commission_percent: Number(percent) || 0 });
      toast.success(isMember ? "Ditandai sebagai member" : "Member dibatalkan");
      load();
    } catch { toast.error("Gagal"); }
  };

  const makeMember = async (c) => {
    const pct = window.prompt("Persentase komisi member (%)", c.commission_percent || 5);
    if (pct === null) return;
    await toggleMember(c, true, Number(pct) || 0);
  };
  const removeMember = async (c) => {
    if (!window.confirm(`Batalkan status member "${c.full_name}"?`)) return;
    await toggleMember(c, false, 0);
  };

  const copyCode = (c) => { navigator.clipboard.writeText(c); toast.success("Kode disalin: " + c); };

  const editCode = async (c) => {
    const nu = window.prompt("Ubah kode referral (huruf/angka):", c.referral_code);
    if (!nu) return;
    try { await api.put(`/customers/${c.id}/referral-code`, { referral_code: nu }); toast.success("Kode diperbarui"); load(); openInfo({ ...c, referral_code: nu.toUpperCase() }); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const editFromInfo = (c) => { setInfoOpen(false); openEdit(c); };

  const filtered = items.filter(c =>
    c.full_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.whatsapp || "").includes(search) || (c.referral_code || "").includes(search.toUpperCase())
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Pelanggan</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Data Penyewa (KYC)</h1>
          <p className="text-sm text-neutral-500 mt-1">Tekan <b>Info</b> untuk melihat data lengkap. Pelanggan terbaru tampil paling atas.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" onClick={openNew} className="text-white" style={{ background: "var(--brand-primary)" }}>
              <Plus className="w-4 h-4 mr-1" /> Daftarkan Penyewa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit Pelanggan" : "Pendaftaran Penyewa"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Nama Lengkap <span className="text-red-600">*</span></Label>
                <Input data-testid="cust-name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} /></div>
              <div><Label>Nomor KTP/SIM</Label>
                <Input data-testid="cust-id" value={form.id_number || ""} onChange={e => setForm({...form, id_number: e.target.value})} /></div>
              <div><Label>Nomor WhatsApp</Label>
                <Input data-testid="cust-wa" placeholder="08xxxxxxxxxx" value={form.whatsapp || ""} onChange={e => setForm({...form, whatsapp: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Foto KTP/SIM</Label>
                <div className="flex items-center gap-3">
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="cust-photo-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}><Camera className="w-4 h-4 mr-1" /> Unggah / Ambil Foto</Button>
                  {form.id_photo && <img src={form.id_photo} alt="ktp" className="h-14 rounded border border-neutral-200" />}
                </div></div>
              <div className="md:col-span-2"><Label>Alamat Rumah</Label>
                <Textarea data-testid="cust-address" value={form.address || ""} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Link Google Maps</Label>
                <Input placeholder="https://maps.app.goo.gl/..." value={form.maps_link || ""} onChange={e => setForm({...form, maps_link: e.target.value})} /></div>
              <div><Label>Media Sosial</Label>
                <Input placeholder="@instagram" value={form.social_media || ""} onChange={e => setForm({...form, social_media: e.target.value})} /></div>
              <div><Label>Sumber Akuisisi</Label>
                <Select value={form.acquisition_source} onValueChange={v => setForm({...form, acquisition_source: v})}>
                  <SelectTrigger data-testid="cust-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...new Set([...SOURCES, form.acquisition_source].filter(Boolean))].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="md:col-span-2"><Label>Kode Referral Teman (opsional)</Label>
                <Input placeholder="Kode member yang mengajak" value={form.referred_by_code || ""} onChange={e => setForm({...form, referred_by_code: e.target.value.toUpperCase()})} /></div>
              {isOwner && (
                <div className="md:col-span-2 border-t border-neutral-200 pt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-md bg-neutral-50 border border-neutral-200">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-red-600" />
                      <div>
                        <div className="font-medium text-sm">Jadikan Member</div>
                        <div className="text-xs text-neutral-500">Dapat komisi otomatis dari transaksi via kode referralnya</div>
                      </div>
                    </div>
                    <Switch checked={!!form.is_member} onCheckedChange={v => setForm({...form, is_member: v})} data-testid="cust-is-member" />
                  </div>
                  {form.is_member && (
                    <div><Label>Persentase Komisi (%)</Label>
                      <Input data-testid="cust-commission" type="number" step="0.5" value={form.commission_percent || 0} onChange={e => setForm({...form, commission_percent: Number(e.target.value)})} />
                      <p className="text-xs text-neutral-500 mt-1">Contoh: 5 → member dapat 5% dari total setiap transaksi yang pakai kodenya.</p>
                    </div>
                  )}
                </div>
              )}
              <div className="md:col-span-2 pt-2">
                <Button data-testid="cust-save" type="submit" className="w-full text-white" style={{background: "var(--brand-primary)"}}>{editing ? "Simpan Perubahan" : "Simpan Pelanggan"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <Input placeholder="Cari nama / WA / kode referral..." value={search} onChange={e => setSearch(e.target.value)} data-testid="cust-search" className="max-w-md" />
      </div>

      <Card className="border-neutral-200 shadow-none">
        <CardContent className="p-0">
          <div className="divide-y divide-neutral-100">
            {filtered.map(c => (
              <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-neutral-50" data-testid={`cust-row-${c.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                    {c.is_member ? <Crown className="w-4 h-4 text-red-600" /> : <User className="w-4 h-4 text-neutral-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-neutral-900 flex items-center gap-1.5 truncate">
                      {c.full_name}
                      {c.is_member && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0" style={{background: "#FEE2E2", color: "#B91C1C"}}>MEMBER {c.commission_percent}%</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openInfo(c)} data-testid={`cust-info-${c.id}`}><Info className="w-3.5 h-3.5 mr-1" /> Info</Button>
                  {isOwner && (c.is_member
                    ? <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeMember(c)} data-testid={`cust-member-${c.id}`}><Crown className="w-3.5 h-3.5 mr-1" /> Member</Button>
                    : <Button variant="ghost" size="sm" onClick={() => makeMember(c)} data-testid={`cust-member-${c.id}`}><Crown className="w-3.5 h-3.5 mr-1" /> Jadi Member</Button>)}
                  {isOwner && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(c)} data-testid={`cust-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="py-12 text-center text-neutral-500">Belum ada pelanggan</div>}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Info Pelanggan */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cust-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {infoCust?.is_member && <Crown className="w-4 h-4 text-red-600" />}
              {infoCust?.full_name || "Detail Pelanggan"}
            </DialogTitle>
          </DialogHeader>
          {infoCust && (
            <div className="space-y-4 text-sm">
              {infoCust.is_member && (
                <div className="flex items-center justify-between p-3 rounded-md bg-red-50 border border-red-100">
                  <span className="font-semibold text-red-800">Member · Komisi {infoCust.commission_percent}%</span>
                  <span className="font-mono-tab font-semibold text-red-700">Terkumpul {fmtIDR(infoCust.commission_earned || 0)}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <InfoRow icon={Phone} label="WhatsApp" value={infoCust.whatsapp || "-"} />
                <InfoRow icon={AtSign} label="Media Sosial" value={infoCust.social_media || "-"} />
                <InfoRow icon={Hash} label="Nomor KTP/SIM" value={infoCust.id_number || "-"} />
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-neutral-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-neutral-500">Alamat</div>
                    <div className="text-neutral-900 break-words">{infoCust.address || "-"}</div>
                    {infoCust.maps_link && <a href={infoCust.maps_link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-red-600 inline-flex items-center gap-1 mt-1" data-testid="info-maps-link">Buka di Maps <ExternalLink className="w-3 h-3" /></a>}
                  </div>
                </div>
                <InfoRow icon={User} label="Sumber Akuisisi" value={infoCust.acquisition_source || "-"} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-md bg-neutral-50 border border-neutral-200">
                <div>
                  <div className="text-xs text-neutral-500">Kode Referral</div>
                  <div className="font-mono-tab font-bold text-base">{infoCust.referral_code || "-"}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => copyCode(infoCust.referral_code)} data-testid="info-copy-code"><Copy className="w-3.5 h-3.5 mr-1" /> Salin</Button>
                  {isOwner && <Button variant="ghost" size="sm" onClick={() => editCode(infoCust)} data-testid="info-edit-code"><Edit2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>

              {infoLoading ? (
                <div className="flex items-center justify-center py-6 text-neutral-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memuat foto...</div>
              ) : infoCust.id_photo ? (
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Foto KTP/SIM</div>
                  <a href={infoCust.id_photo} target="_blank" rel="noreferrer">
                    <img src={infoCust.id_photo} alt="Foto KTP" className="w-full rounded-md border border-neutral-200" data-testid="info-ktp-photo" />
                  </a>
                </div>
              ) : (
                <div className="text-xs text-neutral-400 italic">Belum ada foto KTP/SIM</div>
              )}

              {isOwner && (
                <div className="flex gap-2 pt-2 border-t border-neutral-200">
                  <Button variant="outline" className="flex-1" onClick={() => editFromInfo(infoCust)} data-testid="info-edit-data"><Edit2 className="w-3.5 h-3.5 mr-1" /> Edit Data</Button>
                  <Button variant="outline" className="flex-1 text-red-600" onClick={() => del(infoCust)} data-testid="info-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-neutral-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-neutral-500">{label}</div>
        <div className="text-neutral-900 break-words font-mono-tab">{value}</div>
      </div>
    </div>
  );
}
